import React, { useEffect, useState } from "react";
import {
    CircularProgress, Modal, ModalClose, ModalDialog, Select, Switch
} from "@mui/joy";
import Box from "@mui/joy/Box";
import Input from "@mui/joy/Input";
import Option from "@mui/joy/Option";
import Typography from "@mui/joy/Typography";
import 'react-datepicker/dist/react-datepicker.css';
import Button from "@mui/joy/Button";
import DatePicker from "react-datepicker";
import CustomersTopComponent from "./CustomersTopComponent";

import ApiService from "../../axios/Bookings-Axios";

interface Customer {
    advertisement_platform?: string;
    created_at: string;
    email: string;
    full_name: string;
    house_type: { name: string, id: number };
    id: number;
    notes: string;
    phone: string;
    postcode: string;
    street_address: string;
}

interface ServicePrice {
    id: number;
    service: { id: number; name: string; };
    price: number;
}

interface RecurringBooking {
    active: boolean;
    created_at: string;
    start_date: string | null;
    customer: Customer;
    frequency: string;
    interval: string;
    id: number;
    service: { id: number; name: string; };
    service_price: ServicePrice;
}

function formatDate(old_date: string | Date | null): string {
    if (!old_date) return 'No Valid Date';
    let date = new Date(old_date);

    if (!isNaN(date.getTime())) {
        return date.toLocaleString('en-US', {
            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
            hour: 'numeric', minute: 'numeric', second: 'numeric'
        });
    } else {
        return 'No Valid Date';
    }
}

interface RecurringBookingsModalProps {
    openState: boolean;
    recurringBookingId: number;
    parentCallback: () => void;
}

export default function RecurringBookingsModal({ openState, recurringBookingId, parentCallback }: RecurringBookingsModalProps) {
    const [open, setOpen] = useState(openState);
    const [loadedData, setLoadedData] = useState(false);

    const [customer, setCustomer] = useState<Customer>({
        advertisement_platform: undefined, created_at: "", email: "", full_name: "",
        house_type: { name: 'Detached', id: 1 }, id: 0, notes: "", phone: "",
        postcode: "", street_address: ""
    });

    const [recurringBooking, setRecurringBooking] = useState<RecurringBooking>({
        active: true, created_at: new Date().toISOString(), start_date: new Date().toISOString(),
        customer: customer, frequency: 'week', interval: "4", id: -1,
        service: { id: 0, name: "Roof Clean" },
        service_price: { id: 0, service: { id: 0, name: "Roof Clean" }, price: 15 }
    });

    const handleServiceChange = (newValue: string | null) => {
        if (newValue !== null) {
            const isGutterClean = newValue === 'Gutter Clean' || newValue === 'Conservatory Gutter Clean';
            const frequency = isGutterClean ? 'week' : 'month';
            const interval = isGutterClean ? "4" : "6";
            setRecurringBooking(prev => ({
                ...prev,
                service: { name: newValue, id: -1 },
                frequency: frequency,
                interval: interval
            }));
        }
    };

    useEffect(() => {
        setRecurringBooking(prev => ({ ...prev, customer }));
    }, [customer]);

    useEffect(() => {
        if (!openState) return;
        setLoadedData(false);
        const fetchData = async () => {
            if (recurringBookingId === -1) {
                setLoadedData(true);
                return;
            }
            if (recurringBookingId > 0) {
                try {
                    const recurringBookingLoaded = await ApiService.getRecurringBookingById(recurringBookingId);
                    if (recurringBookingLoaded.customer.notes == null) {
                        recurringBookingLoaded.customer.notes = '';
                    }
                    setCustomer(recurringBookingLoaded.customer);
                    setRecurringBooking(recurringBookingLoaded);
                } catch (error) {
                    console.error("Failed to fetch recurring booking:", error);
                } finally {
                    setLoadedData(true);
                }
            }
        };
        fetchData();
    }, [openState, recurringBookingId]);

    const closeModal = () => {
        setOpen(false);
        parentCallback();
    };

    const handleSave = async (event: React.FormEvent) => {
        event.preventDefault();
        try {
            if (recurringBooking.id !== -1) {
                await ApiService.updateRecurringBooking(recurringBookingId, recurringBooking);
            } else {
                await ApiService.createRecurringBooking(recurringBooking);
            }
            window.location.reload(false);
        } catch (error) {
            console.error("Failed to save recurring booking:", error);
        }
    };

    const setChecked = (checked: boolean) => {
        setRecurringBooking(prev => ({ ...prev, active: checked }));
    };

    const handleTimeChange = (newValue: string | null) => {
        if (newValue) {
            const [interval, frequency] = newValue.split(' ');
            setRecurringBooking(prev => ({ ...prev, frequency, interval }));
        }
    };

    const handleColor = (time: Date) => time.getHours() > 12 ? "" : "text-error";
    const getDefaultTiming = () => `${recurringBooking.interval} ${recurringBooking.frequency}`;
    const handleChangeRecurringBooking = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setRecurringBooking(prev => ({
            ...prev,
            service_price: { ...prev.service_price, price: Number(value) },
        }));
    };

    const getNextBookingCalculate = () => {
        if (!recurringBooking.start_date) return 'N/A';
        let date = new Date(recurringBooking.start_date);
        switch (recurringBooking.frequency) {
            case 'week': date.setDate(date.getDate() + (Number(recurringBooking.interval) * 7)); break;
            case 'month': date.setMonth(date.getMonth() + Number(recurringBooking.interval)); break;
            case 'year': date.setFullYear(date.getFullYear() + Number(recurringBooking.interval)); break;
        }
        return formatDate(date.toISOString());
    };

    return (
        <Modal open={openState} onClose={closeModal}>
            <ModalDialog sx={{ 
                overflowY: 'scroll',
                width: { xs: '95vw', sm: 'auto' },
                maxWidth: { xs: '95vw', sm: '600px' },
                maxHeight: '90vh',
            }}>
                <form onSubmit={handleSave} id="a-form">
                    <ModalClose />
                    <Box sx={{ minWidth: { xs: 'auto', sm: 600 }, display: 'flex', flexDirection: 'column', overflowX: 'hidden' }} />
                    {!loadedData ? (
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', minWidth: '50%', height: '100%', justifyContent: 'center' }}>
                            <CircularProgress size="lg" />
                        </Box>
                    ) : (
                        <Box>
                            <Typography color="danger" sx={{ marginBottom: '5px', width: '100%' }}>
                                * A job will run everyday at midnight to make sure 10 bookings exist for this recurring booking.
                            </Typography>
                            <CustomersTopComponent customer={customer} setCustomer={setCustomer} />
                            <Box sx={{ marginTop: '10px' }}>
                                <Typography variant="soft" sx={{ marginBottom: '5px', width: '100%' }}>Service Selected</Typography>
                                <Select onChange={(e, newValue) => handleServiceChange(newValue)} value={recurringBooking.service.name}>
                                    <Option value="Roof Clean">Roof Clean</Option>
                                    <Option value="Gutter Clean">Gutter Clean</Option>
                                    <Option value="Conservatory Gutter Clean">Conservatory Gutter Clean</Option>
                                </Select>
                            </Box>
                            <Box sx={{ marginTop: '10px' }}>
                                <Typography variant="soft" sx={{ marginBottom: '5px', width: '100%' }}>Recurring Time</Typography>
                                <Select onChange={(e, newValue) => handleTimeChange(newValue)} value={getDefaultTiming()}>
                                    {recurringBooking.service.name === "Roof Clean" ? (
                                        <>
                                            <Option value="2 week">Every 2 Weeks</Option>
                                            <Option value="4 week">Every 4 Weeks</Option>
                                            <Option value="6 week">Every 6 Weeks</Option>
                                            <Option value="8 week">Every 8 Weeks</Option>
                                            <Option value="12 week">Every 12 Weeks</Option>
                                        </>
                                    ) : (
                                        <>
                                            <Option value="3 month">Every 3 Months</Option>
                                            <Option value="4 month">Every 4 Months</Option>
                                            <Option value="6 month">Every 6 Months</Option>
                                            <Option value="12 month">Every 12 Months</Option>
                                        </>
                                    )}
                                </Select>
                            </Box>
                            <Box sx={{ marginTop: '10px' }}>
                                <Typography variant="soft" sx={{ marginBottom: '5px', width: '100%' }}>Service Price</Typography>
                                <Input startDecorator={'£'} type="number" name={'price'} value={recurringBooking?.service_price?.price || ''} onChange={handleChangeRecurringBooking} />
                            </Box>
                            <Box sx={{ marginTop: '10px' }}>
                                <Typography variant="soft" sx={{ marginBottom: '5px', width: '100%' }}>Start Date</Typography>
                                <DatePicker
                                    showTimeSelect
                                    dateFormat="MMMM d, yyyy h:mm aa"
                                    selected={recurringBooking?.start_date ? new Date(recurringBooking.start_date) : new Date()}
                                    onChange={(date) => date && setRecurringBooking(prev => ({ ...prev, start_date: date.toISOString() }))}
                                    timeClassName={handleColor}
                                />
                                <Typography sx={{ marginTop: '5px', width: '100%' }}>The next booking will be created on {getNextBookingCalculate()}</Typography>
                            </Box>
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', marginTop: '15px' }}>
                        {loadedData && (
                            <Switch
                                checked={recurringBooking.active}
                                onChange={(event) => setChecked(event.target.checked)}
                                slotProps={{ track: { children: (<><Typography component="span" level="inherit" sx={{ ml: '10px' }}>On</Typography><Typography component="span" level="inherit" sx={{ mr: '8px' }}>Off</Typography></>) } }}
                                sx={{ marginLeft: 'auto', marginRight: '10px', '--Switch-thumbSize': '27px', '--Switch-trackWidth': '64px', '--Switch-trackHeight': '31px' }}
                            />
                        )}
                        {loadedData && recurringBookingId !== -1 && (<Button type="submit">Save Recurring Booking</Button>)}
                        {loadedData && recurringBookingId === -1 && (<Button type="submit">Create New Recurring Booking</Button>)}
                    </Box>
                </form>
            </ModalDialog>
        </Modal>
    );
}